/**
 * ============================================================================
 * BUDGET SERVICE v16 - FORECAST SUPPORT
 * API Calls für Budget Module (budget.service.js)
 * ✅ v16: + getSummary() für Forecast-Daten
 * ✅ v15: + Sparziele, Daueraufträge, Schulden, Trends
 * ✅ v14: HARDCODED Azure Backend URL
 * ============================================================================
 */

// HARDCODED Azure Backend URL
//const API_URL = 'https://homem-backend.delightfulpebble-aec8e2aa.northeurope.azurecontainerapps.io';

// Mit .ENV datei
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';


console.log('💰 budget.service v16 FORECAST loaded - API_URL:', API_URL);

class BudgetService {
  
  /**
   * Helper: API Call mit Token
   */
  async apiCall(endpoint, method = 'GET', body = null, token = null) {
    try {
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      if (token) {
        options.headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (body) {
        options.body = JSON.stringify(body);
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, options);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('Budget API Error:', error);
      throw error;
    }
  }

  // ============================================
  // CATEGORIES
  // ============================================

  async getCategories(token, type = null) {
    const query = type ? `?type=${type}` : '';
    return await this.apiCall(`/api/budget/categories${query}`, 'GET', null, token);
  }

  async createCategory(token, data) {
    return await this.apiCall('/api/budget/categories', 'POST', data, token);
  }

  async updateCategory(token, id, data) {
    return await this.apiCall(`/api/budget/categories/${id}`, 'PUT', data, token);
  }

  async deleteCategory(token, id) {
    return await this.apiCall(`/api/budget/categories/${id}`, 'DELETE', null, token);
  }

  // ============================================
  // TRANSACTIONS
  // ============================================

  async getTransactions(token, filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.apiCall(`/api/budget/transactions${query}`, 'GET', null, token);
  }

  async createTransaction(token, data) {
    return await this.apiCall('/api/budget/transactions', 'POST', data, token);
  }

  async updateTransaction(token, id, data) {
    return await this.apiCall(`/api/budget/transactions/${id}`, 'PUT', data, token);
  }

  async deleteTransaction(token, id) {
    return await this.apiCall(`/api/budget/transactions/${id}`, 'DELETE', null, token);
  }

  // ============================================
  // BUDGET LIMITS
  // ============================================

  async getLimits(token, filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.apiCall(`/api/budget/limits${query}`, 'GET', null, token);
  }

  async createLimit(token, data) {
    return await this.apiCall('/api/budget/limits', 'POST', data, token);
  }

  async deleteLimit(token, id) {
    return await this.apiCall(`/api/budget/limits/${id}`, 'DELETE', null, token);
  }

  // ✅ v17: Update Limit
  async updateLimit(token, id, data) {
    return await this.apiCall(`/api/budget/limits/${id}`, 'PUT', data, token);
  }

  // ============================================
  // STATISTICS
  // ============================================

  async getStats(token) {
    return await this.apiCall('/api/budget/stats', 'GET', null, token);
  }

  async getMonthlySummary(token, year, month) {
    return await this.apiCall(`/api/budget/summary/${year}/${month}`, 'GET', null, token);
  }

  /**
   * ✅ v16: Get Summary with Forecast data
   * Returns current month summary including forecast
   */
  async getSummary(token) {
    return await this.apiCall('/api/budget/summary', 'GET', null, token);
  }

  // ============================================
  // 🆕 SAVINGS GOALS (v15)
  // ============================================

  /**
   * Get all savings goals
   * @param {string} token - Auth token
   * @param {string} status - 'active', 'completed', 'paused', 'all'
   */
  async getSavingsGoals(token, status = 'active') {
    const query = status !== 'all' ? `?status=${status}` : '';
    return await this.apiCall(`/api/budget/savings${query}`, 'GET', null, token);
  }

  /**
   * Get single savings goal with contributions
   */
  async getSavingsGoal(token, id) {
    return await this.apiCall(`/api/budget/savings/${id}`, 'GET', null, token);
  }

  /**
   * Create new savings goal
   * @param {Object} data - {name, target_amount, monthly_contribution, target_date, icon, color, priority}
   */
  async createSavingsGoal(token, data) {
    return await this.apiCall('/api/budget/savings', 'POST', data, token);
  }

  /**
   * Update savings goal
   */
  async updateSavingsGoal(token, id, data) {
    return await this.apiCall(`/api/budget/savings/${id}`, 'PUT', data, token);
  }

  /**
   * Delete savings goal
   */
  async deleteSavingsGoal(token, id) {
    return await this.apiCall(`/api/budget/savings/${id}`, 'DELETE', null, token);
  }

  /**
   * Add contribution to savings goal
   * @param {Object} data - {amount, note}
   */
  async contributeSavings(token, goalId, data) {
    return await this.apiCall(`/api/budget/savings/${goalId}/contribute`, 'POST', data, token);
  }

  // ============================================
  // 🆕 RECURRING TRANSACTIONS (v15)
  // ============================================

  /**
   * Get all recurring transactions
   */
  async getRecurringTransactions(token) {
    return await this.apiCall('/api/budget/recurring', 'GET', null, token);
  }

  /**
   * Get upcoming scheduled transactions
   * @param {number} days - Days to look ahead (default: 30)
   */
  async getUpcomingTransactions(token, days = 30) {
    return await this.apiCall(`/api/budget/recurring/upcoming?days=${days}`, 'GET', null, token);
  }

  /**
   * Create recurring transaction
   * @param {Object} data - {amount, category, type, frequency, description, start_date, end_date}
   */
  async createRecurringTransaction(token, data) {
    return await this.apiCall('/api/budget/recurring', 'POST', data, token);
  }

  /**
   * Update recurring transaction
   */
  async updateRecurringTransaction(token, id, data) {
    return await this.apiCall(`/api/budget/recurring/${id}`, 'PUT', data, token);
  }

  /**
   * Toggle recurring transaction active/inactive
   */
  async toggleRecurringTransaction(token, id) {
    return await this.apiCall(`/api/budget/recurring/${id}/toggle`, 'PUT', null, token);
  }

  /**
   * Process recurring transaction manually (create actual transaction)
   */
  async processRecurringTransaction(token, id) {
    return await this.apiCall(`/api/budget/recurring/${id}/process`, 'POST', null, token);
  }

  /**
   * Delete recurring transaction
   */
  async deleteRecurringTransaction(token, id) {
    return await this.apiCall(`/api/budget/recurring/${id}`, 'DELETE', null, token);
  }

  // ============================================
  // 🆕 DEBTS (v15)
  // ============================================

  /**
   * Get all debts
   * @param {string} status - 'active', 'paid', 'all'
   * @param {string} type - 'owe', 'lent', 'all'
   */
  async getDebts(token, status = 'active', type = 'all') {
    const params = new URLSearchParams();
    if (status !== 'all') params.append('status', status);
    if (type !== 'all') params.append('type', type);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await this.apiCall(`/api/budget/debts${query}`, 'GET', null, token);
  }

  /**
   * Get single debt with payments
   */
  async getDebt(token, id) {
    return await this.apiCall(`/api/budget/debts/${id}`, 'GET', null, token);
  }

  /**
   * Create debt
   * @param {Object} data - {type, person_name, original_amount, description, due_date, category}
   */
  async createDebt(token, data) {
    return await this.apiCall('/api/budget/debts', 'POST', data, token);
  }

  /**
   * Update debt
   */
  async updateDebt(token, id, data) {
    return await this.apiCall(`/api/budget/debts/${id}`, 'PUT', data, token);
  }

  /**
   * Record payment on debt
   * @param {Object} data - {amount, note}
   */
  async payDebt(token, debtId, data) {
    return await this.apiCall(`/api/budget/debts/${debtId}/pay`, 'POST', data, token);
  }

  /**
   * Delete debt
   */
  async deleteDebt(token, id) {
    return await this.apiCall(`/api/budget/debts/${id}`, 'DELETE', null, token);
  }

  // ============================================
  // 🆕 TRENDS (v15)
  // ============================================

  /**
   * Get monthly trends
   * @param {number} months - Number of months (default: 6)
   */
  async getMonthlyTrends(token, months = 6) {
    return await this.apiCall(`/api/budget/trends/monthly?months=${months}`, 'GET', null, token);
  }

  /**
   * Get category trends
   * @param {number} months - Number of months (default: 3)
   */
  async getCategoryTrends(token, months = 3) {
    return await this.apiCall(`/api/budget/trends/categories?months=${months}`, 'GET', null, token);
  }

  // ============================================
  // BULK OPERATIONS
  // ============================================

  /**
   * Load all budget data at once (v14)
   */
  async loadAllData(token) {
    try {
      const [categories, transactions, limits, stats] = await Promise.all([
        this.getCategories(token),
        this.getTransactions(token),
        this.getLimits(token),
        this.getStats(token).catch(() => ({}))
      ]);

      return {
        categories: categories.success ? categories.categories : [],
        transactions: transactions.success ? transactions.transactions : [],
        limits: limits.success ? limits.limits : [],
        stats: stats.success ? stats : null
      };
    } catch (error) {
      console.error('Error loading all budget data:', error);
      throw error;
    }
  }

  /**
   * 🆕 Load all data including new features (v15)
   */
  async loadAllDataExtended(token) {
    try {
      const [
        categories, 
        transactions, 
        limits, 
        stats,
        savingsGoals,
        recurring,
        debts
      ] = await Promise.all([
        this.getCategories(token),
        this.getTransactions(token),
        this.getLimits(token),
        this.getStats(token).catch(() => ({})),
        this.getSavingsGoals(token).catch(() => ({ savings: [] })),
        this.getRecurringTransactions(token).catch(() => ({ recurring: [] })),
        this.getDebts(token).catch(() => ({ debts: [] }))
      ]);

      return {
        categories: categories.success ? categories.categories : [],
        transactions: transactions.success ? transactions.transactions : [],
        limits: limits.success ? limits.limits : [],
        stats: stats.success ? stats : null,
        savingsGoals: savingsGoals.savings || [],
        recurring: recurring.recurring || [],
        debts: debts.debts || []
      };
    } catch (error) {
      console.error('Error loading extended budget data:', error);
      throw error;
    }
  }

  /**
   * Create default categories
   */
  async createDefaultCategories(token) {
    const defaultCategories = [
      // EINNAHMEN
      { name: 'Gehalt', type: 'income', icon: '💼', color: '#10b981' },
      { name: 'Nebenjob', type: 'income', icon: '💰', color: '#059669' },
      { name: 'Freelance', type: 'income', icon: '💻', color: '#34d399' },
      { name: 'Bonus', type: 'income', icon: '🎁', color: '#6ee7b7' },
      { name: 'Investment', type: 'income', icon: '📈', color: '#a7f3d0' },
      { name: 'Mieteinnahmen', type: 'income', icon: '🏘️', color: '#10b981' },
      { name: 'Kindergeld', type: 'income', icon: '👶', color: '#34d399' },
      { name: 'Steuererstattung', type: 'income', icon: '💸', color: '#6ee7b7' },
      { name: 'Sonstiges Einkommen', type: 'income', icon: '💵', color: '#a7f3d0' },
      
      // WOHNEN (mit is_fixed)
      { name: 'Miete', type: 'expense', icon: '🏠', color: '#ef4444', monthly_limit: 1200, is_fixed: true },
      { name: 'Nebenkosten', type: 'expense', icon: '💡', color: '#dc2626', monthly_limit: 150, is_fixed: true },
      { name: 'Strom', type: 'expense', icon: '⚡', color: '#f87171', monthly_limit: 80, is_fixed: true },
      { name: 'Gas/Heizung', type: 'expense', icon: '🔥', color: '#fca5a5', monthly_limit: 100, is_fixed: true },
      { name: 'Wasser', type: 'expense', icon: '💧', color: '#3b82f6', monthly_limit: 40, is_fixed: true },
      { name: 'Internet/TV', type: 'expense', icon: '📺', color: '#60a5fa', monthly_limit: 50, is_fixed: true },
      { name: 'Telefon/Handy', type: 'expense', icon: '📱', color: '#93c5fd', monthly_limit: 40, is_fixed: true },
      { name: 'GEZ', type: 'expense', icon: '📻', color: '#bfdbfe', monthly_limit: 18.36, is_fixed: true },
      { name: 'Hausrat', type: 'expense', icon: '🛋️', color: '#dbeafe', monthly_limit: 50 },
      
      // LEBENSMITTEL
      { name: 'Lebensmittel', type: 'expense', icon: '🛒', color: '#f59e0b', monthly_limit: 500 },
      { name: 'Restaurant', type: 'expense', icon: '🍽️', color: '#d97706', monthly_limit: 200 },
      { name: 'Café/Bäckerei', type: 'expense', icon: '☕', color: '#fbbf24', monthly_limit: 80 },
      { name: 'Lieferdienst', type: 'expense', icon: '🍕', color: '#fcd34d', monthly_limit: 100 },
      { name: 'Bar/Ausgehen', type: 'expense', icon: '🍺', color: '#fde68a', monthly_limit: 150 },
      
      // TRANSPORT
      { name: 'Auto/KFZ', type: 'expense', icon: '🚗', color: '#8b5cf6', monthly_limit: 300, is_fixed: true },
      { name: 'Benzin/Diesel', type: 'expense', icon: '⛽', color: '#7c3aed', monthly_limit: 200 },
      { name: 'Versicherung Auto', type: 'expense', icon: '🛡️', color: '#a78bfa', monthly_limit: 80, is_fixed: true },
      { name: 'Werkstatt/Reparatur', type: 'expense', icon: '🔧', color: '#c4b5fd', monthly_limit: 100 },
      { name: 'ÖPNV', type: 'expense', icon: '🚌', color: '#ddd6fe', monthly_limit: 60, is_fixed: true },
      { name: 'Taxi/Uber', type: 'expense', icon: '🚕', color: '#ede9fe', monthly_limit: 50 },
      
      // VERSICHERUNGEN (alle fixed)
      { name: 'Krankenversicherung', type: 'expense', icon: '⚕️', color: '#06b6d4', monthly_limit: 400, is_fixed: true },
      { name: 'Haftpflicht', type: 'expense', icon: '🛡️', color: '#0891b2', monthly_limit: 20, is_fixed: true },
      { name: 'Rechtsschutz', type: 'expense', icon: '⚖️', color: '#22d3ee', monthly_limit: 25, is_fixed: true },
      { name: 'Lebensversicherung', type: 'expense', icon: '🏥', color: '#67e8f9', monthly_limit: 50, is_fixed: true },
      
      // GESUNDHEIT
      { name: 'Arzt/Apotheke', type: 'expense', icon: '💊', color: '#ec4899', monthly_limit: 80 },
      { name: 'Fitness/Sport', type: 'expense', icon: '💪', color: '#db2777', monthly_limit: 50, is_fixed: true },
      { name: 'Wellness', type: 'expense', icon: '🧘', color: '#f472b6', monthly_limit: 40 },
      
      // FREIZEIT
      { name: 'Unterhaltung', type: 'expense', icon: '🎬', color: '#14b8a6', monthly_limit: 150 },
      { name: 'Streaming', type: 'expense', icon: '📺', color: '#0d9488', monthly_limit: 40, is_fixed: true },
      { name: 'Hobbys', type: 'expense', icon: '🎨', color: '#2dd4bf', monthly_limit: 100 },
      { name: 'Urlaub/Reisen', type: 'expense', icon: '✈️', color: '#5eead4', monthly_limit: 200 },
      { name: 'Geschenke', type: 'expense', icon: '🎁', color: '#99f6e4', monthly_limit: 80 },
      
      // SHOPPING
      { name: 'Kleidung', type: 'expense', icon: '👕', color: '#f97316', monthly_limit: 150 },
      { name: 'Schuhe', type: 'expense', icon: '👟', color: '#ea580c', monthly_limit: 80 },
      { name: 'Kosmetik/Pflege', type: 'expense', icon: '💄', color: '#fb923c', monthly_limit: 60 },
      { name: 'Elektronik', type: 'expense', icon: '📱', color: '#fdba74', monthly_limit: 100 },
      { name: 'Bücher/Medien', type: 'expense', icon: '📚', color: '#fed7aa', monthly_limit: 50 },
      
      // BILDUNG
      { name: 'Bildung', type: 'expense', icon: '📚', color: '#6366f1', monthly_limit: 100 },
      { name: 'Kurse/Weiterbildung', type: 'expense', icon: '🎓', color: '#4f46e5', monthly_limit: 150 },
      { name: 'Bücher/Lernmaterial', type: 'expense', icon: '📖', color: '#818cf8', monthly_limit: 50 },
      
      // KINDER
      { name: 'Kita/Kindergarten', type: 'expense', icon: '👶', color: '#84cc16', monthly_limit: 300, is_fixed: true },
      { name: 'Schule', type: 'expense', icon: '🎒', color: '#65a30d', monthly_limit: 100 },
      { name: 'Spielzeug', type: 'expense', icon: '🧸', color: '#a3e635', monthly_limit: 50 },
      
      // HAUSTIERE
      { name: 'Haustiere', type: 'expense', icon: '🐕', color: '#a855f7', monthly_limit: 80 },
      { name: 'Tierarzt', type: 'expense', icon: '🏥', color: '#9333ea', monthly_limit: 50 },
      { name: 'Tierfutter', type: 'expense', icon: '🦴', color: '#c084fc', monthly_limit: 60 },
      
      // SPAREN & INVESTIEREN
      { name: 'Sparen', type: 'expense', icon: '💎', color: '#22c55e', monthly_limit: 500 },
      { name: 'ETF/Aktien', type: 'expense', icon: '📈', color: '#16a34a', monthly_limit: 300 },
      { name: 'Altersvorsorge', type: 'expense', icon: '🏦', color: '#4ade80', monthly_limit: 200, is_fixed: true },
      { name: 'Notgroschen', type: 'expense', icon: '🎯', color: '#86efac', monthly_limit: 100 },
      
      // SONSTIGES
      { name: 'Spenden', type: 'expense', icon: '❤️', color: '#78716c', monthly_limit: 50 },
      { name: 'Sonstiges', type: 'expense', icon: '📋', color: '#9e9e9e' }
    ];

    try {
      const created = [];
      for (const cat of defaultCategories) {
        const result = await this.createCategory(token, cat);
        if (result.success) {
          created.push(result.category);
        }
      }
      return created;
    } catch (error) {
      console.error('Error creating default categories:', error);
      throw error;
    }
  }
}

// Export singleton instance
const budgetService = new BudgetService();
export default budgetService;